import { ApolloClient, createHttpLink, from, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { InMemoryCache } from '@apollo/client/cache';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { fromPromise } from '@apollo/client/link/utils';
import type { AuthCore } from '../auth/authCore';
import { env } from '../config/env';

const URL = `${env.API_URL}/graphql`;

/**
 * Creates an Apollo Client with proper authentication and error handling.
 *
 * @param auth - AuthCore instance for token management and refresh logic
 * @returns Configured Apollo Client instance
 */
export function createApolloClient(auth: AuthCore) {
  const httpLink = createHttpLink({ uri: URL });

  // Auth link: attach current token to every request
  const authLink = setContext(async (_, { headers: existingHeaders }) => {
    const headers = {
      ...existingHeaders,
      Accept: 'application/json',
    };

    const token = auth.getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return { headers };
  });

  // Error link: handle 401s with refresh + retry (ONCE per operation)
  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    // Check if we already retried this operation
    const alreadyTried = operation.getContext()._authTried as boolean | undefined;

    // Check for 401 in both HTTP and GraphQL errors
    const is401 =
      (networkError as any)?.statusCode === 401 ||
      graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED');

    // Don't retry if: not a 401, or already tried once
    if (!is401 || alreadyTried) {
      // Log non-auth errors
      if (networkError && !is401) {
        console.error(`[Network error]: ${networkError}`);
      }
      if (graphQLErrors && !is401) {
        for (const { message, locations, path } of graphQLErrors) {
          console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
        }
      }
      return; // Don't retry
    }

    // Mark operation as retried to prevent infinite loops
    operation.setContext({ _authTried: true });

    // CRITICAL: Return the observable for proper Apollo retry semantics
    return fromPromise(
      auth.ensureFreshToken().catch(() => ({ ok: false as const }))
    ).flatMap((result) => {
      if (result.ok) {
        // Token refreshed - retry the operation with new auth header
        return forward(operation);
      } else {
        // Refresh failed, logged out - complete without retry
        return Observable.of();
      }
    });
  });

  // Retry link: automatically retry network failures with exponential backoff
  const retryLink = new RetryLink({
    delay: {
      initial: 300,  // Start with 300ms delay
      max: 3000,     // Max 3 seconds between retries
      jitter: true   // Add randomness to prevent thundering herd
    },
    attempts: {
      max: 3,        // Retry up to 3 times
      retryIf: (error) => {
        // Only retry network errors, not GraphQL errors
        return !!error && !error.result;
      }
    }
  });

  return new ApolloClient({
    link: from([errorLink, retryLink, authLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Team: {
          keyFields: ['id'],
          fields: {
            organization: {
              merge(existing, incoming) {
                return incoming || existing;
              },
            },
          },
        },
        Query: {
          fields: {
            teamById: {
              merge(existing, incoming, { mergeObjects }) {
                if (!incoming) return existing;
                if (!existing) return incoming;

                // If incoming is just a reference, preserve existing full object
                if (incoming.__ref && !incoming.id) {
                  return existing;
                }

                // If existing is a reference and incoming is full object, use incoming
                if (existing.__ref && incoming.id) {
                  return incoming;
                }

                // Both are full objects, merge them safely
                return mergeObjects(existing, incoming);
              },
            },
          },
        },
      },
    }),
    defaultOptions: {
      query: {
        fetchPolicy: 'cache-first',
      },
    },
    connectToDevTools: false,
  });
}

// Export a default client for backward compatibility during migration
// This will be removed once App.tsx is updated
let defaultClient: ApolloClient<any> | null = null;

export function setDefaultClient(client: ApolloClient<any>) {
  defaultClient = client;
}

export default defaultClient as any;
